import { getInteractionModality, setInteractionModality } from '@react-aria/interactions';
import type { Modality } from '@react-aria/interactions';
import { useLayoutEffect, useRef } from 'react';

import { overlayCloseReasons } from './overlayCloseReasons';

// Keys that never establish keyboard modality on their own, so they don't count as the user
// driving the overlay by keyboard. Escape is excluded separately — it's the dismiss gesture
// this hook exists to treat as a pointer action.
const MODIFIER_KEYS = new Set<string>(['Shift', 'Control', 'Alt', 'Meta']);

let cancelPendingEscapePin: (() => void) | null = null;

interface UsePreserveOpenModalityOptions {
  enabled?: boolean;
}

function pinPointerThroughEscapeKeyUp() {
  if (cancelPendingEscapePin != null) {
    cancelPendingEscapePin();
  }

  setInteractionModality('pointer');

  function cleanup() {
    document.removeEventListener('keyup', handleKeyUp);
    window.removeEventListener('blur', cleanup);

    if (cancelPendingEscapePin === cleanup) {
      cancelPendingEscapePin = null;
    }
  }

  function handleKeyUp(event: KeyboardEvent) {
    if (event.key !== 'Escape') {
      return;
    }

    // React Aria handles keyup during capture and sets keyboard modality. This bubble listener
    // runs afterward and restores the pointer modality selected for this Escape dismissal.
    setInteractionModality('pointer');
    cleanup();
  }

  // These listeners intentionally outlive the closing overlay and clean themselves up after the
  // Escape press completes or the window loses focus before keyup.
  document.addEventListener('keyup', handleKeyUp);
  window.addEventListener('blur', cleanup, { once: true });
  cancelPendingEscapePin = cleanup;
}

/**
 * Keeps a mouse/touch-opened overlay from showing a focus ring when it's dismissed with
 * Escape — on the restored trigger, and on the item still focused inside it during an exit
 * animation. Returns a handler the overlay calls on close; for a pointer-opened overlay closed
 * by Escape with no keyboard navigation in between, it pins the modality back to 'pointer'.
 *
 * The non-obvious part: react-aria sets modality to 'keyboard' on both keydown AND keyup
 * (capture, on document), so pinning must survive both the whole Escape press and the overlay
 * component unmounting. A self-cleaning bubble-phase keyup listener re-asserts 'pointer' after
 * react-aria's capture handler. Genuine keyboard use (tracked via a capture keydown listener,
 * ignoring Escape and lone modifiers) skips the pin so a real keyboard ring is preserved.
 *
 * @param options.enabled - the overlay's restoreFocus flag; no restore, no ring.
 * @returns handler to call with the overlay's close reason.
 */
export function usePreserveOpenModality({ enabled = true }: UsePreserveOpenModalityOptions = {}) {
  const openModalityRef = useRef<Modality | null>(null);
  const usedKeyboardRef = useRef(false);
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  useLayoutEffect(() => {
    openModalityRef.current = getInteractionModality();
    usedKeyboardRef.current = false;

    function handleKeyDownCapture(event: KeyboardEvent) {
      if (event.key === 'Escape' || MODIFIER_KEYS.has(event.key)) {
        return;
      }

      usedKeyboardRef.current = true;
    }

    document.addEventListener('keydown', handleKeyDownCapture, true);

    return () => {
      document.removeEventListener('keydown', handleKeyDownCapture, true);
    };
  }, []);

  function preserveModalityOnClose(reason: string) {
    // Only ever suppress a ring (force 'pointer'); never force 'keyboard', so we never add a
    // spurious ring after a mouse action. Pin only when a pointer-opened overlay is dismissed
    // by Escape and the user never drove it by keyboard — the sole case where Escape flips the
    // modality to 'keyboard' despite the interaction being pointer-only.
    const shouldPreservePointer =
      enabledRef.current &&
      reason === overlayCloseReasons.ESCAPE &&
      openModalityRef.current === 'pointer' &&
      usedKeyboardRef.current === false;

    if (!shouldPreservePointer) {
      return;
    }

    pinPointerThroughEscapeKeyUp();
  }

  return preserveModalityOnClose;
}
